/**
 * Utility functions for status badges and labels
 */

/**
 * Get CSS class for appointment status badge
 * @param status - The appointment status
 * @param includePrefix - Whether to include 'status-badge' prefix (default: false)
 * @returns CSS class string for the status badge
 */
export const getAppointmentStatusBadgeClass = (status: string, includePrefix: boolean = false): string => {
  const statusLower = status.toLowerCase();
  const prefix = includePrefix ? 'status-badge ' : '';
  
  switch (statusLower) {
    case 'scheduled':
    case 'confirmed':
      return `${prefix}status-upcoming`;
    case 'completed':
      return `${prefix}status-completed`;
    case 'cancelled':
    case 'missed':
    case 'reschedule_pending':
      return `${prefix}status-cancelled`;
    default:
      return includePrefix ? 'status-badge' : '';
  }
};

/**
 * Get display label for appointment status
 * @param status - The appointment status
 * @returns Human-readable status label
 */
export const getAppointmentStatusLabel = (status: string): string => {
  const statusLower = status.toLowerCase();
  switch (statusLower) {
    case 'scheduled':
      return 'Scheduled';
    case 'confirmed':
      return 'Confirmed';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'missed':
      return 'Missed';
    case 'reschedule_pending':
      return 'Reschedule pending';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  }
};

/**
 * Get CSS class for AI request status badge
 * @param status - The AI request status
 * @param includePrefix - Whether to include 'status-badge' prefix (default: true)
 * @returns CSS class string for the status badge
 */
export const getAIRequestStatusBadgeClass = (status: string, includePrefix: boolean = true): string => {
  const statusLower = status.toLowerCase();
  const prefix = includePrefix ? 'status-badge ' : '';
  
  if (statusLower === 'requested' || statusLower === 'pending') {
    return `${prefix}status-pending`;
  }
  if (statusLower === 'completed') {
    return `${prefix}status-completed`;
  }
  if (statusLower === 'rejected' || statusLower === 'declined') {
    return `${prefix}status-declined`;
  }
  return includePrefix ? 'status-badge' : '';
};

/**
 * Get display label for AI request status
 * @param status - The AI request status
 * @returns Human-readable status label
 */
export const getAIRequestStatusLabel = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'requested') return 'Requested';
  if (statusLower === 'pending') return 'Pending';
  if (statusLower === 'completed') return 'Completed';
  if (statusLower === 'rejected') return 'Declined';
  return status.charAt(0).toUpperCase() + status.slice(1);
};
